import { useState } from "react";
import { toast } from "sonner";
import { Wallet, Landmark, CreditCard } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const METHOD_LABELS = {
    bank: { label: "Bank-Überweisung", Icon: Landmark },
    paypal: { label: "PayPal", Icon: CreditCard },
    points_only: { label: "Nur Punkte sammeln", Icon: Wallet },
};

/**
 * Vermittler payout-request form. Nutzt die in den Auszahlungs-Einstellungen
 * (BankForm) gewählte bevorzugte Methode — KEINE doppelte Methoden-Auswahl mehr.
 */
export default function PayoutForm({ available, onSubmitted, payoutMethod = "bank" }) {
    const [points, setPoints] = useState("");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const disabledMethod = payoutMethod === "points_only";
    const m = METHOD_LABELS[payoutMethod] || METHOD_LABELS.bank;

    const submit = async () => {
        const n = Number(points);
        if (!Number.isInteger(n) || n <= 0) {
            toast.error("Bitte eine ganze Punktezahl > 0 eingeben");
            return;
        }
        if (n > available) {
            toast.error(`Maximal ${available} verfügbar`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/vermittler/payout-request", { points: n, method: payoutMethod, note: note || null });
            toast.success("Auszahlungsanfrage gesendet");
            setPoints("");
            setNote("");
            onSubmitted();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-4" data-testid="payout-form">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[#FFCC00]" />
                    <h2 className="font-display font-bold text-base">Punkte einlösen</h2>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-600" data-testid="payout-method-label">
                    <m.Icon className="w-3.5 h-3.5" /> Auszahlung via {m.label}
                </span>
            </div>
            {disabledMethod ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="payout-disabled-note">
                    Auszahlung deaktiviert — in den Auszahlungs-Einstellungen ist „Nur Punkte sammeln" gewählt.
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Punkte (max. {available})</Label>
                            <Input
                                type="number"
                                min="1"
                                max={available}
                                value={points}
                                onChange={(e) => setPoints(e.target.value)}
                                placeholder={`max. ${available}`}
                                className="rounded-sm font-mono-num mt-1"
                                disabled={!available || submitting}
                                data-testid="payout-points-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Notiz (optional)</Label>
                            <Input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="z. B. Verrechnung mit Gebühren"
                                className="rounded-sm mt-1"
                                disabled={submitting}
                                data-testid="payout-note-input"
                            />
                        </div>
                    </div>
                    <Button
                        onClick={submit}
                        disabled={!available || submitting || !points}
                        className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider disabled:opacity-50"
                        data-testid="payout-submit-btn"
                    >
                        {submitting ? "Sende…" : "Auszahlung anfordern"}
                    </Button>
                </>
            )}
        </div>
    );
}
